import { TFunction } from 'i18next'
import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { useIBRoutesConditions } from '@pages/IntroducingBroker/IntroducingBrokerRoutes'

import { useModal } from '@context/ModalsContext'

import { Modal } from '@ui/components/Modal'
import { PageSection } from '@ui/components/PageSection'
import { CirclePlusIcon } from '@ui/icons/CirclePlus.icon'
import { SummaryItem, TableSummary } from '@ui/v2/TableSummary/TableSummary'

import { commissionTiers } from '@utils/MultiTier'

import { AssignedCommissionPlan } from '@model/MultiTierDto'

import { ScrollToIds } from '@hooks/useScrollToElementIds'

import { Box } from '@shared/components/Box'
import { InformationModal } from '@shared/modals/Information.modal'

import { CommissionPlansTable } from './CommissionPlansTable'
import { AddEditNewPlanModal } from './modals/AddEditNewPlan.modal'
import { CommissionPlanData, useCommissionPlans } from './useCommissionPlans'
import { useCommissionPlansSummary } from './useCommissionPlansSummary'

import styles from './CommissionPlansPage.module.scss'

export const CommissionPlansPage: FC = () => {
  const { t } = useTranslation()
  const { isLastMultiTierLevel } = useIBRoutesConditions()

  const { summaryItems, summaryData, isLoading: isSummaryLoading } = useCommissionPlansSummary()
  const {
    commissionPlans,
    isLoading: isCommissionPlansLoading,
    handleCommissionPlan,
  } = useCommissionPlans()
  const addEditNewPlanModal = useModal(AddEditNewPlanModal, {
    summary: commissionTiers(summaryData),
    commissionPlans: summaryData ?? [],
    onSubmit: handleCommissionPlan,
  })
  const commissionPlansModal = useModal(InformationModal, {
    title: t('IB.MultiTier.IB Commission Plans'),
    description: () => (
      <Box direction='column' flex gap='x16'>
        <Modal.Body.Text>
          {t('IB.MultiTier.Create and manage up to 3 Commission Plans')}
        </Modal.Body.Text>

        {summaryItems.map((plan) => (
          <Modal.Body.TitleTextBox key={plan.title}>
            <Modal.Body.Text medium>{plan.title}</Modal.Body.Text>
            <Modal.Body.Text>
              {t('IB.MultiTier.Commission that will be assigned for 1 lot', {
                level: plan.title,
              })}
            </Modal.Body.Text>
          </Modal.Body.TitleTextBox>
        ))}
        <Modal.Body.Text>
          {t('IB.MultiTier.After you create an IB Commission Plan')}
        </Modal.Body.Text>
      </Box>
    ),
  })

  const handleEdit = (group: AssignedCommissionPlan) => {
    addEditNewPlanModal.open({
      commissionPlan: group,
    })
  }

  return (
    <div className={styles.commissionPlansPage}>
      <TableSummary
        isLoading={isSummaryLoading}
        summaryItems={translateSummaryItems(summaryItems, t)}
      />
      {isLastMultiTierLevel === false && (
        <div className={styles.container}>
          <PageSection
            title={t('IB.MultiTier.IB Commission Plans')}
            id={ScrollToIds.MultiTierCommissionPlans}
            renderActions={[
              {
                icon: CirclePlusIcon,
                onClick: () => addEditNewPlanModal.open(),
                title: t('IB.MultiTier.Add new Plan'),
                disabled: commissionPlans.length === 4,
              },
            ]}
            infoData={() => commissionPlansModal.open()}
          />

          <CommissionPlansTable
            isLoading={isCommissionPlansLoading}
            commissionPlansSummary={summaryItems}
            commissionPlans={translateCommissionPlans(commissionPlans, t)}
            onEdit={handleEdit}
          />
        </div>
      )}
    </div>
  )
}

const translateCommissionPlans = (
  commissionPlans: CommissionPlanData[],
  t: TFunction<'translation', undefined>
): CommissionPlanData[] => {
  const commissionPlansDefaultTranslation = {
    'Default Plan': t('IB.MultiTier.IB Commission Plan Default'),
    'Plan 1': t('IB.MultiTier.IB Commission Plan 1'),
    'Plan 2': t('IB.MultiTier.IB Commission Plan 2'),
    'Plan 3': t('IB.MultiTier.IB Commission Plan 3'),
  }

  return commissionPlans.map((commissionPlan: CommissionPlanData) => ({
    ...commissionPlan,
    name:
      commissionPlansDefaultTranslation[
        commissionPlan.name as keyof typeof commissionPlansDefaultTranslation
      ] || commissionPlan.name,
  }))
}

const translateSummaryItems = (
  summaryItems: SummaryItem[],
  t: TFunction<'translation', undefined>
): SummaryItem[] => {
  const summaryItemsTranslation = {
    Classic: t('IB.MultiTier.Classic'),
    Raw: t('IB.MultiTier.Raw'),
    Pro: t('IB.MultiTier.Pro'),
    VIP: t('IB.MultiTier.VIP'),
  }

  return summaryItems.map((summaryItem: SummaryItem) => ({
    ...summaryItem,
    title:
      summaryItemsTranslation[summaryItem.title as keyof typeof summaryItemsTranslation] ||
      summaryItem.title,
  }))
}
